import { SymphonyIcon } from "@assets/icons";
import Button from "@common/components/Button";
import Dialog from "@common/components/Dialog";
import DialogActions from "@common/components/Dialog/DialogActions";
import DialogTitle from "@common/components/Dialog/DialogTitle";
import { useErrorNotification } from "@common/components/Notification";
import { KeyboardArrowRight } from "@mui/icons-material";
import { DialogContent, DialogContentText, Skeleton } from "@mui/material";
import { getTroubleshoot } from "@services/aiTroubleshoot";
import { TroubleshootRequest } from "@services/aiTroubleshoot/types";
import { Device } from "@services/devices/types";
import { Ticket } from "@services/tickets/types";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

type TroubleshootingDialogProps = {
  troubleshoot?: string;
  setTroubleshoot: (value: string | undefined) => void;
  device?: Device;
  ticket?: Ticket;
  addToTimeline?: (message: string) => void;
};

const TroubleshootingDialog = (props: TroubleshootingDialogProps) => {
  const { setTroubleshoot, troubleshoot, device, ticket, addToTimeline } =
    props;
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingMessage, setLoadingMessage] = useState<boolean>(false);
  const [message, setMessage] = useState<string>("");
  const [currentText, setCurrentText] = useState<string>("");
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const dispatchError = useErrorNotification();
  const resetValues = () => {
    setLoadingMessage(true);
    setLoading(true);
    setCurrentText("");
    setCurrentIndex(0);
  };

  const loadMessage = async () => {
    if (device !== undefined && troubleshoot !== undefined) {
      try {
        const r = await getTroubleshoot(
          {
            device: device as TroubleshootRequest["device"],
            account: { id: device?.room?.location?.account?.id as string },
            error: troubleshoot !== "" ? troubleshoot : "Unreachable",
            ticket,
          },
          page
        );
        setMessage(r.message);
      } catch (e) {
        dispatchError(e);
      }
      setLoading(false);
    }
  };

  useEffect(() => {
    reloadMessage();
  }, [page]);

  const reloadMessage = async () => {
    resetValues();
    loadMessage();
  };

  useEffect(() => {
    if (troubleshoot !== undefined) {
      loadMessage();
    } else {
      resetValues();
      return;
    }
  }, [troubleshoot]);

  // Display message char by char
  useEffect(() => {
    if (!loading && currentIndex < message.length) {
      const textTimeout = setTimeout(() => {
        setCurrentText(currentText + message[currentIndex]);
        setCurrentIndex(currentIndex + 1);
      }, 1);
      return () => {
        clearTimeout(textTimeout);
      };
    } else if (!loading) {
      setLoadingMessage(false);
      return;
    } else {
      return;
    }
  }, [currentIndex, loading]);

  return (
    <Dialog
      maxWidth="md"
      open={troubleshoot !== undefined}
      onClose={() => setTroubleshoot(undefined)}
      fullWidth
    >
      <DialogTitle>
        <SymphonyIcon
          sx={{ color: ({ palette }) => palette.common.grey.light }}
        />
      </DialogTitle>
      <DialogContent
        sx={{ bgcolor: ({ palette }) => palette.background.paper, mx: 3 }}
      >
        <DialogContentText
          sx={{
            height: "40rem",
            whiteSpace: "pre-line",
          }}
        >
          {loading
            ? [...new Array(10)].map((_, i) => (
                <Skeleton key={`ske_${i}`} variant="text" height="4rem" />
              ))
            : currentText}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        {page > 0 && (
          <Button
            onClick={() => {
              setPage(page - 1);
            }}
          >
            {t("Back")}
          </Button>
        )}
        <Button
          disabled={loadingMessage}
          endIcon={<KeyboardArrowRight />}
          variant="outlined"
          onClick={() => {
            setPage(page + 1);
          }}
          ignoreLocalization
        >
          {t("Need more help?")}
        </Button>
        {addToTimeline && (
          <Button
            disabled={loadingMessage}
            endIcon={<KeyboardArrowRight />}
            variant="outlined"
            onClick={() => addToTimeline(message)}
            ignoreLocalization
          >
            {t("Add to Timeline")}
          </Button>
        )}
        <Button
          variant="outlined"
          color="greyed"
          onClick={() => {
            setTroubleshoot(undefined);
          }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TroubleshootingDialog;
